import React, { useState, useRef, useCallback, useEffect } from "react";
import { getTopZ } from "./zOrder";
import { createPortal } from "react-dom";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ScatterChart, Scatter, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { BarChart2, X, Minus, Maximize2 } from "lucide-react";

const MIN_W = 400;
const MIN_H = 320;
const DEFAULT_W = 640;
const DEFAULT_H = 460;

const PALETTE = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#f97316", "#ec4899", "#14b8a6", "#a3e635",
];

// ── Chart renderers ────────────────────────────────────────────────────────────

function renderBar({ data, xKey, yKeys, stacked }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={{ fontSize: 12 }} />
        {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {yKeys.map((k, i) => (
          <Bar key={k} dataKey={k} fill={PALETTE[i % PALETTE.length]} stackId={stacked ? "s" : undefined} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function renderLine({ data, xKey, yKeys }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={{ fontSize: 12 }} />
        {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {yKeys.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} dot={data.length < 30} strokeWidth={2} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function renderArea({ data, xKey, yKeys, stacked }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={{ fontSize: 12 }} />
        {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {yKeys.map((k, i) => (
          <Area key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]}
            fill={PALETTE[i % PALETTE.length]} fillOpacity={0.15}
            stackId={stacked ? "s" : undefined} strokeWidth={2} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function renderPie({ data, nameKey, valueKey }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey={valueKey} nameKey={nameKey} cx="50%" cy="50%"
          outerRadius="70%" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function renderScatter({ data, xKey, yKey }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} name={xKey} tick={{ fontSize: 11 }} />
        <YAxis dataKey={yKey} name={yKey} tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={{ fontSize: 12 }} cursor={{ strokeDasharray: "3 3" }} />
        <Scatter data={data} fill={PALETTE[0]} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function renderTable({ data }) {
  if (!data || data.length === 0) return <div style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>No data</div>;
  const cols = Object.keys(data[0]);
  return (
    <div style={{ overflow: "auto", height: "100%", fontSize: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 400 }}>
        <thead>
          <tr style={{ background: "#f1f5f9", position: "sticky", top: 0 }}>
            {cols.map(c => (
              <th key={c} style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap", fontWeight: 600 }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
              {cols.map(c => (
                <td key={c} style={{ padding: "5px 10px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row[c] == null ? <span style={{ color: "#94a3b8" }}>null</span> : String(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderMetrics({ metrics }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, padding: 16 }}>
      {metrics.map((m, i) => (
        <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{m.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: PALETTE[i % PALETTE.length] }}>
            {m.value}{m.unit ? <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 3 }}>{m.unit}</span> : null}
          </div>
          {m.change != null && (
            <div style={{ fontSize: 11, color: m.changeDirection === "up" ? "#10b981" : m.changeDirection === "down" ? "#ef4444" : "#94a3b8", marginTop: 2 }}>
              {m.changeDirection === "up" ? "↑" : m.changeDirection === "down" ? "↓" : ""} {m.change}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ChartContent({ config }) {
  if (!config) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 13 }}>No chart data</div>;
  const pad = config.type === "table" || config.type === "metrics" ? 0 : "8px 4px 4px 4px";
  try {
    switch (config.type) {
      case "bar":     return <div style={{ width: "100%", height: "100%", padding: pad }}>{renderBar(config)}</div>;
      case "line":    return <div style={{ width: "100%", height: "100%", padding: pad }}>{renderLine(config)}</div>;
      case "area":    return <div style={{ width: "100%", height: "100%", padding: pad }}>{renderArea(config)}</div>;
      case "pie":     return <div style={{ width: "100%", height: "100%", padding: pad }}>{renderPie(config)}</div>;
      case "scatter": return <div style={{ width: "100%", height: "100%", padding: pad }}>{renderScatter(config)}</div>;
      case "table":   return <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>{renderTable(config)}</div>;
      case "metrics": return <div style={{ width: "100%", height: "100%", padding: pad }}>{renderMetrics(config)}</div>;
      default:        return <div style={{ padding: 16, color: "#ef4444", fontSize: 13 }}>Unknown chart type: {config.type}</div>;
    }
  } catch (err) {
    return <div style={{ padding: 16, color: "#ef4444", fontSize: 13 }}>Chart error: {err.message}</div>;
  }
}

// ── Single chart window instance ───────────────────────────────────────────────

function ChartWindow({ id, config, initialPos, onClose }) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [pos, setPos] = useState(initialPos);
  const [zIndex, setZIndex] = useState(() => getTopZ());

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;
    const onMove = (e) => setPos({
      x: Math.max(0, Math.min(window.innerWidth - size.w, e.clientX - startX)),
      y: Math.max(0, Math.min(window.innerHeight - 48, e.clientY - startY)),
    });
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [pos, size.w]);

  const handleResizeStart = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const startW = size.w, startH = size.h;
    const startPosX = pos.x, startPosY = pos.y;
    const onMove = (e) => {
      const dx = e.clientX - startX, dy = e.clientY - startY;
      let newW = startW, newH = startH, newX = startPosX, newY = startPosY;
      if (direction.includes("e")) newW = Math.max(MIN_W, startW + dx);
      if (direction.includes("w")) { newW = Math.max(MIN_W, startW - dx); newX = startPosX + (startW - newW); }
      if (direction.includes("s")) newH = Math.max(MIN_H, startH + dy);
      if (direction.includes("n")) { newH = Math.max(MIN_H, startH - dy); newY = startPosY + (startH - newH); }
      setSize({ w: newW, h: newH });
      setPos({ x: newX, y: newY });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [size, pos]);

  const windowH = isMinimized ? 48 : size.h;

  return (
    <div
      onMouseDown={() => setZIndex(getTopZ())}
      className="fixed flex flex-col rounded-xl shadow-2xl border border-slate-200 overflow-hidden bg-card-bg"
      style={{ left: pos.x, top: pos.y, width: size.w, height: windowH, minWidth: MIN_W, minHeight: isMinimized ? 48 : MIN_H, zIndex }}
    >
      {/* Title bar */}
      <div
        onMouseDown={handleDragStart}
        className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-700 to-slate-800 cursor-grab active:cursor-grabbing select-none flex-shrink-0"
        style={{ height: 48 }}
      >
        <BarChart2 className="w-4 h-4 text-white/80 flex-shrink-0" />
        <span className="text-white text-sm font-medium flex-1 truncate">{config.title}</span>
        {config.subtitle && (
          <span className="text-white/50 text-xs truncate max-w-[160px]">{config.subtitle}</span>
        )}
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setIsMinimized(v => !v)}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
          title={isMinimized ? "Restore" : "Minimize"}
        >
          {isMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
        </button>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => onClose(id)}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body: optional analyst review text + chart area */}
      {!isMinimized && (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {config.review && (
            <div
              className="px-3 py-2 text-xs text-text-secondary border-b border-border-subtle whitespace-pre-wrap overflow-y-auto"
              style={{ maxHeight: "40%", lineHeight: 1.5 }}
            >
              {config.review}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ChartContent config={config} />
          </div>
        </div>
      )}

      {/* Resize handles */}
      {!isMinimized && (
        <>
          <div onMouseDown={(e) => handleResizeStart(e, "n")} className="absolute top-0 left-2 right-2 h-1 cursor-n-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "s")} className="absolute bottom-0 left-2 right-2 h-1 cursor-s-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "w")} className="absolute top-2 bottom-2 left-0 w-1 cursor-w-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "e")} className="absolute top-2 bottom-2 right-0 w-1 cursor-e-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "nw")} className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "ne")} className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "sw")} className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize" />
          <div onMouseDown={(e) => handleResizeStart(e, "se")} className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize" />
        </>
      )}
    </div>
  );
}

// ── Manager: renders all open chart windows via portal ─────────────────────────

// Stagger new windows so they don't stack exactly on top of each other
function calcInitialPos(index) {
  const base = {
    x: Math.max(24, window.innerWidth - DEFAULT_W - 470),
    y: Math.max(24, window.innerHeight - DEFAULT_H - 24),
  };
  const offset = (index % 5) * 28;
  return { x: base.x - offset, y: base.y - offset };
}

export default function ChartViewerManager({ charts, onClose }) {
  if (!charts || charts.length === 0) return null;

  return createPortal(
    <>
      {charts.map((item, index) => (
        <ChartWindow
          key={item.id}
          id={item.id}
          config={item.config}
          initialPos={item.pos || calcInitialPos(index)}
          onClose={onClose}
        />
      ))}
    </>,
    document.body
  );
}
